import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import './Chart.css';
import { useTheme } from '../contexts/ThemeContext';
import { getChartJsOptions } from '../Utils/chartTheme';
import { formatCurrency } from '../Utils/chartUtils';

Chart.register(annotationPlugin); 

interface ChartProps {
  timeSeriesData: number[][];
  withdrawalRate: number;
  contributionsEndAge: string;
  contributionsEndAgePerson1: string;
  contributionsEndAgePerson2: string;
  withdrawalStartAge: string;
  withdrawalStartAgePerson1: string;
  withdrawalStartAgePerson2: string;
  currentAge: string;
  currentAgePerson1: string;
  currentAgePerson2: string;
  personsToggle: boolean
}

const SimplifiedRetirementIncomeChart: React.FC<ChartProps> = React.memo(({ 
    timeSeriesData, 
    withdrawalRate, 
    contributionsEndAge,
    contributionsEndAgePerson1,
    contributionsEndAgePerson2,
    withdrawalStartAge,
    withdrawalStartAgePerson1,
    withdrawalStartAgePerson2,
    currentAge,
    currentAgePerson1,
    currentAgePerson2,
    personsToggle
}) => {
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  
  const formatDollar = useCallback((value: number) => {
    return formatCurrency(value);
  }, []);

  const calculateYield = useCallback((balance: number) => {
    return balance * (withdrawalRate / 100);
  }, [withdrawalRate]);


  // Memoize chart data computation for performance
  const chartData = useMemo(() => {
    try {
      // Compute the number of years needed to include all events
      const timeUntilLastEvent = !personsToggle ?
        Math.max(parseInt(withdrawalStartAge), parseInt(contributionsEndAge)) - parseInt(currentAge) 
        : Math.max(
          Math.max(parseInt(withdrawalStartAgePerson1), parseInt(contributionsEndAgePerson1)) - parseInt(currentAgePerson1), 
          Math.max(parseInt(withdrawalStartAgePerson2), parseInt(contributionsEndAgePerson2)) - parseInt(currentAgePerson2)
        );

      // Continue the timeries 10 years after the last event
      const timeseriesLength = Math.max(timeUntilLastEvent + 10, 1); 

      // Create the chart data, filling in missing values with the last available value, since the 4% rule assumes a constant withdrawal rate that doesn't change the balance
      return Array.from({ length: timeseriesLength }, (_, index) => {
        const year = new Date().getFullYear() + index;
        const person1Balance = timeSeriesData[0]?.[index] ?? timeSeriesData[0]?.[timeSeriesData[0].length - 1] ?? 0;
        const person2Balance = timeSeriesData[1] 
          ? (timeSeriesData[1][index] ?? timeSeriesData[1][timeSeriesData[1].length - 1] ?? 0)
          : 0;

        return {
          year,
          person1Balance,
          person2Balance,
        };
      });
    } catch (error) {
      console.warn('Error computing retirement chart data:', error);
      return [];
    }
  }, [timeSeriesData, withdrawalStartAge, contributionsEndAge, withdrawalStartAgePerson1, contributionsEndAgePerson1, withdrawalStartAgePerson2, contributionsEndAgePerson2, currentAge, currentAgePerson1, currentAgePerson2, personsToggle]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const calculateYear = useCallback((age: string, currentAge: string) => {
    return currentYear + (parseInt(age) - parseInt(currentAge));
  }, [currentYear]);

  // Chart creation and update effect
  useEffect(() => {
    if (!chartRef.current || !chartData || chartData.length === 0) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const options = getChartJsOptions(isDark, formatCurrency);
    
    // Get reference years for contribution end and withdrawal start markers
    const contributionEndYears = [];
    const withdrawalStartYears = [];
    if (personsToggle) {
      contributionEndYears.push(
        calculateYear(contributionsEndAgePerson1, currentAgePerson1),
        calculateYear(contributionsEndAgePerson2, currentAgePerson2)
      );
      withdrawalStartYears.push(
        calculateYear(withdrawalStartAgePerson1, currentAgePerson1),
        calculateYear(withdrawalStartAgePerson2, currentAgePerson2)
      );
    } else {
      contributionEndYears.push(calculateYear(contributionsEndAge, currentAge));
      withdrawalStartYears.push(calculateYear(withdrawalStartAge, currentAge));
    }
    
    // Custom options for this chart
    const customOptions = {
      ...options,
      scales: {
        ...options.scales,
        x: {
          ...options.scales.x,
          title: {
            display: false
          },
          ticks: {
            ...options.scales.x.ticks,
            callback: function(value: any, index: number) {
              const year = chartData[index]?.year;
              return year ? year.toString().substr(-2) + "'" : '';
            }
          }
        },
        y: {
          ...options.scales.y,
          title: {
            display: true,
            text: 'Retirement Savings (Today\'s Dollars)',
            color: isDark ? 'hsl(0 0% 98%)' : 'hsl(222.2 84% 4.9%)'
          },
          stacked: true
        }
      },
      plugins: {
        ...options.plugins,
        tooltip: {
          ...options.plugins.tooltip,
          callbacks: {
            title: function(context: any) {
              return `Year: ${context[0].label}`;
            },
            label: function(context: any) {
              const datasetLabel = context.dataset.label || '';
              const value = formatCurrency(context.parsed.y);
              return `${datasetLabel}: ${value}`;
            },
            afterBody: function(tooltipItems: any) {
              const total = tooltipItems.reduce((sum: number, item: any) => {
                return sum + item.parsed.y;
              }, 0);
              const annualYield = calculateYield(total);
              return [`Annual Yield: ${formatCurrency(annualYield)}`];
            }
          }
        },
        annotation: {
          annotations: {
            ...contributionEndYears.reduce((annotations: any, year: number, index: number) => {
              const annotationId = `contributions-end-${index}`;
              annotations[annotationId] = {
                type: 'line',
                xMin: year,
                xMax: year,
                borderColor: '#A0A0A0',
                borderWidth: 2,
                borderDash: [8, 4],
                label: {
                  display: true,
                  content: 'Contributions End',
                  position: 'start',
                  backgroundColor: 'rgba(160, 160, 160, 0.8)',
                  color: '#fff',
                  font: {
                    size: 10
                  }
                }
              };
              return annotations;
            }, {}),
            ...withdrawalStartYears.reduce((annotations: any, year: number, index: number) => {
              const annotationId = `withdrawals-start-${index}`;
              annotations[annotationId] = {
                type: 'line',
                xMin: year,
                xMax: year,
                borderColor: '#FFD700',
                borderWidth: 2,
                borderDash: [4, 2],
                label: {
                  display: true,
                  content: 'Withdrawals Start',
                  position: 'end',
                  backgroundColor: 'rgba(255, 215, 0, 0.8)',
                  color: '#000',
                  font: {
                    size: 10
                  }
                }
              };
              return annotations;
            }, {})
          }
        }
      },
      interaction: {
        intersect: false,
        mode: 'index' as const
      }
    };

    // Prepare datasets
    const datasets = [
      {
        label: 'Person 1',
        data: chartData.map(d => d.person1Balance),
        fill: true,
        backgroundColor: 'rgba(76, 175, 80, 0.6)',
        borderColor: '#4CAF50',
        borderWidth: 2,
        tension: 0.1
      }
    ];

    if (timeSeriesData[1]) {
      datasets.push({
        label: 'Person 2',
        data: chartData.map(d => d.person2Balance),
        fill: true,
        backgroundColor: 'rgba(33, 150, 243, 0.6)',
        borderColor: '#2196F3',
        borderWidth: 2,
        tension: 0.1
      });
    }

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: chartData.map(d => d.year),
        datasets: datasets
      },
      options: customOptions
    });
  }, [chartData, isDark, timeSeriesData, personsToggle, contributionsEndAge, contributionsEndAgePerson1, contributionsEndAgePerson2, withdrawalStartAge, withdrawalStartAgePerson1, withdrawalStartAgePerson2, currentAge, currentAgePerson1, currentAgePerson2, formatDollar, calculateYear, calculateYield]);

  // Error boundary fallback
  if (!chartData || chartData.length === 0) {
    return (
      <div className="chart-container">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <p>No data available for chart</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="chart-container">
        <div className="w-full h-full">
          <canvas ref={chartRef}></canvas>
        </div>
      </div>
      <div className="chart-legend" style={{ marginTop: '10px', fontSize: '12px', color: '#999', textAlign: 'center' }}>
        <span style={{ marginRight: '15px' }}>
          <span style={{ 
            display: 'inline-block', 
            width: '12px', 
            height: '12px', 
            border: '2px solid #A0A0A0', 
            marginRight: '5px' 
          }}></span>
          Contributions End
        </span>
        <span>
          <span style={{ 
            display: 'inline-block', 
            width: '10px', 
            height: '10px', 
            background: 'radial-gradient(circle at 30% 30%, #FFF7D6, #FFD700 45%, #B8860B)', 
            borderRadius: '50%', 
            marginRight: '5px' 
          }}></span>
          Withdrawals Start
        </span>
      </div>
    </>
  );
});

export default SimplifiedRetirementIncomeChart;
